import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, ChevronLeft, Check } from "lucide-react";

const COURSES = ["AI & Machine Learning", "Cybersecurity", "Data Science", "Data Analytics", "Cloud & Networking", "Not Sure Yet"];
const ACADEMICS = ["12th Pass", "Currently in College", "Graduated", "Working Professional"];
const START_OPTIONS = ["Immediately", "Within 1 Month", "In 2-3 Months", "Just Exploring"];
const TIMES = ["10 AM – 12 PM", "12 PM – 2 PM", "2 PM – 4 PM", "4 PM – 6 PM", "6 PM – 8 PM"];

const STEP_TITLES = [
  "Your Contact Details",
  "Which course are you interested in?",
  "Please share your academics",
  "When do you want to start learning?",
  "Select a time slot",
];

const STEP_KEYS = [null, "interest", "stage", "start_when", "best_time"];

const initialForm = { name: "", phone: "", email: "", interest: "", stage: "", start_when: "", best_time: "" };

const validPhone = (v) => /^(\+?91)?[6-9]\d{9}$/.test(v.replace(/[\s\-()]/g, ""));
const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

const Field = ({ label, error, children, testId }) => (
  <div data-testid={testId}>
    <label className="block text-sm font-medium text-white mb-1 sm:mb-1.5">{label}</label>
    {children}
    {error && <p className="mt-1 text-xs" style={{ color: "var(--error)" }} data-testid={`${testId}-error`}>{error}</p>}
  </div>
);

const Option = ({ label, selected, onClick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="w-full rounded-xl px-4 py-3 sm:py-3.5 text-left text-sm font-medium flex items-center justify-between transition-colors duration-200"
    style={{
      background: selected ? "rgba(14, 217, 181, 0.12)" : "var(--bg-primary)",
      border: `1px solid ${selected ? "var(--accent)" : "var(--input-border)"}`,
      color: selected ? "#FFFFFF" : "var(--text-body)",
    }}
  >
    {label}
    {selected && <Check size={16} style={{ color: "var(--accent)" }} strokeWidth={3} />}
  </button>
);

export const LeadModal = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setStep(1);
      setDir(1);
      setForm(initialForm);
      setServerError("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const step1Valid = form.name.trim().length >= 2 && validPhone(form.phone) && validEmail(form.email);
  const stepKey = STEP_KEYS[step - 1];
  const currentValid = step === 1 ? step1Valid : Boolean(stepKey && form[stepKey]);

  const goNext = () => { setDir(1); setStep((s) => s + 1); };
  const goBack = () => { setDir(-1); setStep((s) => s - 1); };

  const selectOption = (key, value) => {
    setForm({ ...form, [key]: value });
    if (step < 5) setTimeout(goNext, 280);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setServerError("");
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, city: "" }),
      });
      if (!res.ok) throw new Error("submit failed");
      onClose();
      navigate("/thank-you", { state: { interest: form.interest, best_time: form.best_time } });
    } catch {
      setServerError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const inputCls = (bad) => `lead-input w-full rounded-lg px-4 py-2.5 sm:py-3 text-sm ${bad ? "input-error" : ""}`;
  const stepFields = ["", "lead-interest-field", "lead-stage-field", "lead-start-field", "lead-time-field"];

  return (
    <div className="modal-shell fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="lead-modal" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0" style={{ background: "rgba(13, 17, 23, 0.75)", backdropFilter: "blur(8px)" }} onClick={onClose} />
      <div
        className="modal-panel relative w-full sm:max-w-md sm:mx-4 rounded-t-2xl sm:rounded-2xl p-4 pt-5 sm:p-7 overflow-y-auto"
        style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }}
      >
        <button
          data-testid="modal-close-button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3.5 right-3.5 p-1.5 rounded-full transition-colors hover:text-white"
          style={{ color: "var(--text-muted)" }}
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-3 mb-3 pr-9">
          {step > 1 ? (
            <button
              data-testid="lead-back-button"
              onClick={goBack}
              aria-label="Back"
              className="p-1.5 rounded-full transition-colors hover:text-white shrink-0"
              style={{ color: "var(--text-muted)", border: "1px solid var(--input-border)" }}
            >
              <ChevronLeft size={16} />
            </button>
          ) : (
            <span className="w-[30px] shrink-0" />
          )}
          <div className="flex-1">
            <div className="h-1 rounded-full overflow-hidden" style={{ background: "var(--input-border)" }} data-testid="wizard-progress">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${(step / 5) * 100}%`, background: "var(--accent)" }}
              />
            </div>
          </div>
          <span className="text-xs font-semibold shrink-0" style={{ color: "var(--accent)" }} data-testid="wizard-step-label">
            Step {step} of 5
          </span>
        </div>

        <h3 className="font-display text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-5 pr-2" data-testid="lead-modal-title">
          {STEP_TITLES[step - 1]}
        </h3>

        <div key={step} className={dir === 1 ? "step-forward" : "step-back"} data-testid={stepFields[step - 1] || "lead-contact-step"}>
          {step === 1 && (
            <div className="space-y-3 sm:space-y-3.5 min-h-0 sm:min-h-[320px]">
              <Field label="Full Name" error={form.name && form.name.trim().length < 2 ? "Please enter your full name" : ""} testId="lead-name-field">
                <input data-testid="lead-name-input" type="text" value={form.name} onChange={set("name")} placeholder="e.g. Aarav Sharma"
                  className={inputCls(form.name && form.name.trim().length < 2)} />
              </Field>
              <Field label="Phone Number" error={form.phone && !validPhone(form.phone) ? "Enter a valid 10-digit mobile number" : ""} testId="lead-phone-field">
                <input data-testid="lead-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="e.g. 98109 81090"
                  className={inputCls(form.phone && !validPhone(form.phone))} />
              </Field>
              <Field label="Email" error={form.email && !validEmail(form.email) ? "Enter a valid email address" : ""} testId="lead-email-field">
                <input data-testid="lead-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com"
                  className={inputCls(form.email && !validEmail(form.email))} />
              </Field>
            </div>
          )}

          {step > 1 && (
            <div className="space-y-2 sm:space-y-2.5 min-h-0 sm:min-h-[320px]">
              {(step === 2 ? COURSES : step === 3 ? ACADEMICS : step === 4 ? START_OPTIONS : TIMES).map((opt, i) => (
                <Option
                  key={opt}
                  testId={`lead-option-${i}`}
                  label={opt}
                  selected={form[stepKey] === opt}
                  onClick={() => selectOption(stepKey, opt)}
                />
              ))}
            </div>
          )}
        </div>

        {serverError && <p className="mt-3 text-sm" style={{ color: "var(--error)" }} data-testid="lead-server-error">{serverError}</p>}

        {step < 5 ? (
          <button
            data-testid="lead-next-button"
            onClick={goNext}
            disabled={!currentValid}
            className="cta-button w-full rounded-full py-3 sm:py-3.5 mt-4 sm:mt-5 text-sm sm:text-base font-bold disabled:opacity-40 disabled:pointer-events-none"
          >
            Next
          </button>
        ) : (
          <button
            data-testid="lead-submit-button"
            onClick={handleSubmit}
            disabled={!currentValid || submitting}
            className="cta-button w-full rounded-full py-3 sm:py-3.5 mt-4 sm:mt-5 text-sm sm:text-base font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none"
          >
            {submitting && <Loader2 size={18} className="animate-spin" />}
            Book My Free Session
          </button>
        )}
        <p className="mt-2.5 sm:mt-3 text-center text-xs" style={{ color: "var(--text-muted)" }}>
          Your details are 100% confidential. No spam, ever.
        </p>
      </div>
    </div>
  );
};
